import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true, // 🔥 CRITICAL: Tells the browser to send HttpOnly cookies with every request
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(); // No token to pass, cookies handle themselves
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Ignore 401s from login/refresh endpoints to prevent infinite loops
    if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== '/token' && originalRequest.url !== '/refresh') {
      
      // ==========================================
      // 1. CANDIDATE 401 HANDLING
      // ==========================================
      // If the failing request was for a candidate endpoint, they don't have a refresh 
      // token flow in the backend right now. Just bounce them securely to their login.
      if (originalRequest.url.includes('/candidate/')) {
        localStorage.removeItem('candidate_email');
        localStorage.removeItem('candidate_name');
        window.location.href = '/candidate-login';
        return Promise.reject(error);
      }

      // ==========================================
      // 2. RECRUITER 401 HANDLING (Refresh Token)
      // ==========================================
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      isRefreshing = true;

      try {
        // Hit the refresh endpoint using the dynamic baseURL, not hardcoded localhost!
        // Axios will automatically attach the HttpOnly refresh_token cookie.
        await axios.post(`${api.defaults.baseURL}/refresh`, {}, { 
            withCredentials: true 
        });

        // If successful, the browser automatically saves the NEW access_token cookie
        processQueue(null);
        
        // Retry the original request (it will automatically use the new cookie)
        return api(originalRequest);
        
      } catch (refreshError) {
        // Refresh failed (refresh token expired too). Log them out.
        processQueue(refreshError);
        localStorage.removeItem('isAuthenticated'); // CLEAR THE FLAG
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Normal error handling for non-401s (e.g., 500 Server Error, 404 Not Found)
    return Promise.reject(error);
  }
);

export default api;